import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Avatar, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon, Badge as StaffIcon, Add as AddIcon,
  Edit as EditIcon, PersonOff as DeactivateIcon,
  Restore as ReactivateIcon,
  DeleteForever as DeleteForeverIcon,
  Send as SendIcon,
  Visibility, VisibilityOff,
} from '@mui/icons-material';

import ClinicTagChipField from '../components/ClinicTagChipField';
import {
  generateUniqueStrongPassword,
  normalizeTags,
  formatTagTitleCase,
} from '../clinicUserFormUtils';
import { PASSWORD_RULES_TEXT, passwordProblem, apiErrorMessage } from '../passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function TagsCell({ tags }) {
  const list = normalizeTags(tags);
  if (!list.length) return '—';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 280 }}>
      {list.map((t) => (
        <Chip key={t} size="small" label={t} variant="outlined" sx={{ fontWeight: 500 }} />
      ))}
    </Box>
  );
}

export default function StaffList() {
  const [staffList, setStaffList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rowBusy, setRowBusy] = useState(null); // { id, action } while a row action runs
  const [editStaff, setEditStaff] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFieldLocked, setPasswordFieldLocked] = useState(true);
  const passwordAutoGenDoneRef = useRef(false);

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    speciality: [],
    information: '',
    ehr_staff_id: '',
  });

  const loadStaff = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/admin/staff`);
      setStaffList(res.data.staff);
      setFiltered(res.data.staff);
    } catch {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFiltered(staffList);
      return;
    }
    const q = searchTerm.toLowerCase();
    setFiltered(
      staffList.filter((s) => {
        const spec = normalizeTags(s.speciality).join(' ').toLowerCase();
        const ehr = (s.ehr_staff_id || '').toLowerCase();
        return (
          (s.full_name || '').toLowerCase().includes(q) ||
          (s.email || '').toLowerCase().includes(q) ||
          spec.includes(q) ||
          (s.information || '').toLowerCase().includes(q) ||
          ehr.includes(q)
        );
      }),
    );
  }, [searchTerm, staffList]);

  const openCreate = () => {
    setEditStaff(null);
    passwordAutoGenDoneRef.current = false;
    setPasswordFieldLocked(true);
    setShowPassword(false);
    setForm({
      full_name: '',
      email: '',
      password: '',
      speciality: [],
      information: '',
      ehr_staff_id: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditStaff(s);
    setForm({
      full_name: s.full_name || '',
      email: s.email || '',
      password: '',
      speciality: normalizeTags(s.speciality),
      information: s.information || '',
      ehr_staff_id: s.ehr_staff_id || '',
    });
    setDialogOpen(true);
  };

  const handlePasswordFocus = useCallback(() => {
    if (passwordAutoGenDoneRef.current) return;
    setForm((prev) => {
      if (prev.password) return prev;
      if (passwordAutoGenDoneRef.current) return prev;
      passwordAutoGenDoneRef.current = true;
      return { ...prev, password: generateUniqueStrongPassword() };
    });
  }, []);

  const handleGeneratePasswordClick = useCallback(() => {
    setPasswordFieldLocked(false);
    passwordAutoGenDoneRef.current = true;
    setForm((prev) => ({ ...prev, password: generateUniqueStrongPassword() }));
  }, []);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (editStaff) {
        const body = {
          speciality: form.speciality,
        };
        if (form.full_name) body.full_name = form.full_name;
        if (form.email && form.email !== editStaff.email) body.email = form.email;
        if (form.information !== undefined) body.information = form.information;
        body.ehr_staff_id = form.ehr_staff_id?.trim() || null;
        await axios.put(`${API_URL}/api/v1/admin/staff/${editStaff.id}`, body);
        toast.success('Staff member updated');
      } else {
        const payload = {
          full_name: form.full_name,
          email: form.email,
          speciality: form.speciality,
          information: form.information?.trim() || null,
          ehr_staff_id: form.ehr_staff_id?.trim() || null,
        };
        if (form.password?.trim()) {
          const problem = passwordProblem(form.password);
          if (problem) {
            toast.error(problem);
            return;
          }
          payload.password = form.password;
        }
        const res = await axios.post(`${API_URL}/api/v1/admin/staff`, payload);
        toast.success(res.data?.message || 'Staff member added');
      }
      setDialogOpen(false);
      loadStaff();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (s) => {
    if (rowBusy || !window.confirm(`Deactivate ${s.full_name}? They will not be able to sign in until reactivated.`)) return;
    setRowBusy({ id: s.id, action: 'deactivate' });
    try {
      await axios.delete(`${API_URL}/api/v1/admin/staff/${s.id}`);
      toast.success('Staff member deactivated');
      loadStaff();
    } catch {
      toast.error('Failed to deactivate');
    } finally {
      setRowBusy(null);
    }
  };

  const handleReactivate = async (s) => {
    if (rowBusy || !window.confirm(`Reactivate ${s.full_name}? They will be able to sign in again.`)) return;
    setRowBusy({ id: s.id, action: 'reactivate' });
    try {
      await axios.put(`${API_URL}/api/v1/admin/staff/${s.id}`, { is_active: true });
      toast.success('Staff member reactivated');
      loadStaff();
    } catch {
      toast.error('Failed to reactivate');
    } finally {
      setRowBusy(null);
    }
  };

  const handlePermanentDelete = async (s) => {
    if (rowBusy || !window.confirm(
        `Permanently delete ${s.full_name} (${s.email})?\n\nThis removes their account. This cannot be undone.`,
      )) {
      return;
    }
    setRowBusy({ id: s.id, action: 'delete' });
    try {
      await axios.delete(`${API_URL}/api/v1/admin/staff/${s.id}/permanent`);
      toast.success('Staff member permanently deleted');
      loadStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setRowBusy(null);
    }
  };

  const handleResendInvite = async (s) => {
    if (rowBusy) return;
    setRowBusy({ id: s.id, action: 'resend' });
    try {
      const res = await axios.post(`${API_URL}/api/v1/admin/staff/${s.id}/resend-invite`);
      toast.success(res.data?.message || 'Invite re-sent');
      loadStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    } finally {
      setRowBusy(null);
    }
  };

  const createDisabled = !form.full_name?.trim() || !form.email?.trim();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}><StaffIcon /></Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700}>Staff</Typography>
            <Typography variant="body2" color="text.secondary">Front desk, medical assistants, and support staff</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Staff</Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search by name, email, EHR ID, role tags, or information…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Role / tags</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>EHR ID</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6}>Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6}>No staff members found.</TableCell></TableRow>
            ) : (
              filtered.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell><Typography fontWeight={600}>{s.full_name || '—'}</Typography></TableCell>
                  <TableCell><TagsCell tags={s.speciality} /></TableCell>
                  <TableCell>{s.email}</TableCell>
                  <TableCell>{s.ehr_staff_id || '—'}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      <Chip size="small" label={s.is_active ? 'Active' : 'Inactive'} color={s.is_active ? 'success' : 'default'} />
                      {s.invite_pending && (
                        <Chip size="small" label="Invite pending" color="warning" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={s.invite_pending
                      ? 'Resend invite (fresh 48-hour link)'
                      : 'Already set up — this staff member has account access, so there is no invite to resend'}>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={!s.invite_pending || rowBusy?.id === s.id}
                          onClick={() => handleResendInvite(s)}
                        >
                          {rowBusy?.id === s.id && rowBusy?.action === 'resend' ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <span>
                        <IconButton size="small" disabled={rowBusy?.id === s.id} onClick={() => openEdit(s)}><EditIcon /></IconButton>
                      </span>
                    </Tooltip>
                    {s.is_active ? (
                      <Tooltip title="Deactivate (can reactivate later)">
                        <span>
                          <IconButton size="small" color="warning" disabled={rowBusy?.id === s.id} onClick={() => handleDeactivate(s)}>
                            {rowBusy?.id === s.id && rowBusy?.action === 'deactivate' ? <CircularProgress size={18} color="inherit" /> : <DeactivateIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Reactivate">
                        <span>
                          <IconButton size="small" color="success" disabled={rowBusy?.id === s.id} onClick={() => handleReactivate(s)}>
                            {rowBusy?.id === s.id && rowBusy?.action === 'reactivate' ? <CircularProgress size={18} color="inherit" /> : <ReactivateIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete permanently">
                      <span>
                        <IconButton size="small" color="error" disabled={rowBusy?.id === s.id} onClick={() => handlePermanentDelete(s)}>
                          {rowBusy?.id === s.id && rowBusy?.action === 'delete' ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => { if (!saving) setDialogOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle>{editStaff ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Full Name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            margin="normal"
            required
          />
          <ClinicTagChipField
            label="Role & tags"
            required={false}
            value={form.speciality}
            onChange={(tags) => setForm({ ...form, speciality: tags })}
            helperText="Optional. e.g. Front desk, Medical assistant. Press Enter or comma after each. Word capitalization is applied automatically."
            placeholder="e.g. Front desk"
            guardAutofill
            inputName="staff-speciality-tags"
            formatTag={formatTagTitleCase}
          />
          <TextField
            fullWidth
            label="Information"
            value={form.information}
            onChange={(e) => setForm({ ...form, information: e.target.value })}
            margin="normal"
            multiline
            minRows={2}
            placeholder="Short internal notes"
          />
          <TextField
            fullWidth
            label="EHR staff identifier"
            value={form.ehr_staff_id}
            onChange={(e) => setForm({ ...form, ehr_staff_id: e.target.value })}
            margin="normal"
            placeholder="Optional — ID from your EHR for this staff member"
            helperText="Optional. Use when your EHR tracks non-clinician users."
            inputProps={{ autoComplete: 'off', name: 'staff-ehr-id' }}
          />
          {!editStaff && (
            <Box>
              <TextField
                fullWidth
                label="Password (optional)"
                name="new-staff-password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') {
                    passwordAutoGenDoneRef.current = false;
                  }
                  setForm({ ...form, password: v });
                }}
                onFocus={() => {
                  if (passwordFieldLocked) setPasswordFieldLocked(false);
                  handlePasswordFocus();
                }}
                margin="normal"
                helperText={`Leave blank to email an invitation — they set their own password from the link. Or generate or enter a password and it is emailed to them for immediate access; they'll be asked to choose their own when they first sign in. ${PASSWORD_RULES_TEXT}`}
                inputProps={{
                  autoComplete: 'new-password',
                  readOnly: passwordFieldLocked,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setShowPassword((x) => !x)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={handleGeneratePasswordClick}
                sx={{ mt: 0.5, mb: 1 }}
              >
                Generate strong password
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              saving || (
                editStaff
                  ? !form.full_name?.trim() || !form.email?.trim()
                  : createDisabled
              )
            }
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? (editStaff ? 'Saving…' : 'Creating…') : (editStaff ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
